import { getUserSession, isEqualObject } from 'ui-smartforce-settings';
import {
  AnalyticsConfig,
  AnalyticsFiltersRequest,
  AnalyticsPeriodOptions,
  Widget,
  WidgetBase
} from '../../../Models';
import { WIDGET_RATE_CARD, WIDGET_TYPES } from '../../../Constants/Analytics';
import { apiGet, apiPost } from '../../../Helpers';
import { APP_CONTACTS_API_BASE_URL } from '../../../Constants/Url';
import { getWidgetWithData } from '../Common/Common';

const LOCAL_STORAGE_KEY = 'Smartforce.CitizenContact.WidgetsFavourites';

export class AnalyticsServiceClass {
  private config: AnalyticsConfig | undefined;
  private widgetListLite: WidgetBase[] = [];
  private filters: AnalyticsFiltersRequest | undefined;

  getConfig() {
    return this.config;
  }

  getWidgetListLite() {
    return this.widgetListLite;
  }

  async updateConfig(
    filters?: AnalyticsFiltersRequest
  ): Promise<AnalyticsConfig> {
    if (this.config && isEqualObject(filters, this.filters)) {
      return Promise.resolve(this.config);
    } else {
      const url = `${APP_CONTACTS_API_BASE_URL}/analytics/generate`;
      const period = filters?.period || 'month';

      const fetchResp = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `bearer ${getUserSession().access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(
          filters
            ? {
                period: filters.period,
                period_value: filters.period_value,
                filters:
                  Object.keys(filters.filters).length > 0
                    ? filters.filters
                    : undefined
              }
            : { period }
        )
      });

      if (fetchResp.ok) {
        return fetchResp.json().then((config: AnalyticsConfig) => {
          let widgets: Widget[] = [];
          this.widgetListLite = [];

          config.widgets
            .filter((w) => WIDGET_TYPES.includes(w.component.name))
            .forEach((w: Widget, index: number) => {
              widgets = [...widgets, getWidgetWithData(config, w.id, index)];

              this.widgetListLite = [
                ...this.widgetListLite,
                {
                  id: w.id,
                  title: w.title,
                  type: w.component.name
                }
              ];
            });

          this.config = { ...config, period, widgets };
          this.filters = filters;

          return this.config;
        });
      } else {
        const body = await fetchResp.json();
        return Promise.reject({
          code: fetchResp.status,
          text: fetchResp.statusText,
          detail: body.detail
        });
      }
    }
  }

  getWidget(id: number): Widget | undefined {
    return this.config
      ? (this.config.widgets.find((w: Widget) => w.id === id) as Widget)
      : undefined;
  }

  getWidgets(ids: number[]): Widget[] {
    if (!this.config) return [];
    else {
      return ids
        .map((id: number) => this.getWidget(id))
        .filter((w) => !!w) as Widget[];
    }
  }

  getNewWidgets(): WidgetBase[] {
    if (!this.config) return [];

    const removeSuggestions = this.widgetListLite.filter(
      (w) =>
        !this.config?.suggested_widgets.find((value: Number) => w.id === value)
    );

    const newRateCards = removeSuggestions.filter(
      (w) => w.type === WIDGET_RATE_CARD
    );

    let newWidgets = newRateCards.slice(newRateCards.length - 5);

    const newOthers = removeSuggestions.filter(
      (w) => w.type !== WIDGET_RATE_CARD
    );

    newWidgets = [...newWidgets, ...newOthers.slice(newOthers.length - 5)];

    return newWidgets;
  }

  getSuggestions(): WidgetBase[] {
    let result: WidgetBase[] = [];
    if (this.config?.suggested_widgets) {
      for (let id of this.config.suggested_widgets) {
        const widget = this.widgetListLite.find((w) => w.id === id);
        if (widget) {
          result = [...result, widget];
        }
      }
    }
    return result;
  }

  getLSFavourites(): number[] {
    const favourites = localStorage.getItem(LOCAL_STORAGE_KEY);
    return favourites ? JSON.parse(favourites) : [];
  }

  async getFavourites(): Promise<number[]> {
    const url = `${APP_CONTACTS_API_BASE_URL}/analytics/me/settings`;
    const favourites = await apiGet<number[]>(
      url,
      getUserSession().access_token
    );
    const lsFavourites = this.getLSFavourites();

    if (lsFavourites.length > 0 && !isEqualObject(favourites, lsFavourites)) {
      this.saveFavourites(lsFavourites);
      return lsFavourites;
    } else {
      if (lsFavourites.length === 0) {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(favourites));
      }
      return favourites;
    }
  }

  async saveFavourites(widgets: number[]): Promise<{ widgets: number[] }> {
    const url = `${APP_CONTACTS_API_BASE_URL}/analytics/me/settings`;

    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(widgets));
    return apiPost(url, { widgets }, getUserSession().access_token);
  }

  isWidgetStorageEvent(e: StorageEvent): boolean {
    return e.key === LOCAL_STORAGE_KEY;
  }

  async getPeriods(): Promise<AnalyticsPeriodOptions> {
    const url = `${APP_CONTACTS_API_BASE_URL}/analytics/periods`;
    return apiGet(url, getUserSession().access_token);
  }
}

const AnalyticsService = new AnalyticsServiceClass();

export default AnalyticsService;
